import { useMemo } from 'react';
import axios, { AxiosInstance } from 'axios';
import { API_BASE, API_AUTH } from 'utils/constants';

// Create request methods for an instance
const createRequestMethods = (instance: AxiosInstance) => ({
  get: async <R>(url: string, config: any = {}): Promise<R | null> => {
    const token = localStorage.getItem('access_token')?.replace(/"/g, '');
    const headers = {
      ...config?.headers,
      'Content-Type': 'application/json',
      authorization: token ? `Bearer ${token}` : '',
    };
    const { data } = await instance.get<R>(url, { ...config, headers });

    return data;
  },
  post: async <R>(url: string, data?: any, config: any = {}): Promise<R | null> => {
    const token = localStorage.getItem('access_token')?.replace(/"/g, '');
    const headers = {
      ...config?.headers,
      'Content-Type': 'application/json',
      authorization: token ? `Bearer ${token}` : '',
    };
    const { data: responseData } = await instance.post<R>(url, data, {
      ...config,
      headers,
    });

    return responseData;
  },
  put: async <R>(url: string, data?: any, config: any = {}): Promise<R | null> => {
    const token = localStorage.getItem('access_token')?.replace(/"/g, '');
    const headers = {
      ...config?.headers,
      'Content-Type': 'application/json',
      authorization: token ? `Bearer ${token}` : '',
    };
    const { data: responseData } = await instance.put<R>(url, data, {
      ...config,
      headers,
    });

    return responseData;
  },
  delete: async <R>(url: string, config: any = {}): Promise<R | null> => {
    const token = localStorage.getItem('access_token')?.replace(/"/g, '');
    const headers = {
      ...config?.headers,
      'Content-Type': 'application/json',
      authorization: token ? `Bearer ${token}` : '',
    };
    const { data: responseData } = await instance.delete<R>(url, { ...config, headers });

    return responseData;
  },
  patch: async <R>(url: string, data?: any, config: any = {}): Promise<R | null> => {
    const token = localStorage.getItem('access_token')?.replace(/"/g, '');
    const headers = {
      ...config?.headers,
      'Content-Type': 'application/json',
      authorization: token ? `Bearer ${token}` : '',
    };
    const { data: responseData } = await instance.patch<R>(url, data, {
      ...config,
      headers,
    });

    return responseData;
  },
});

// Create a single base instance
const defaultBaseInstance = axios.create({ baseURL: API_BASE });
// Memoized default request methods
const defaultRequest = createRequestMethods(defaultBaseInstance);

// Create an Auth instance
const authAxiosInstance = axios.create({ baseURL: API_AUTH });
createRequestMethods(authAxiosInstance);

// Hook supports both default and custom configurations
export const useApi = (baseURL?: string, withAuth: boolean = true) => {
  return useMemo(() => {
    if (!baseURL) {
      return defaultRequest;
    }

    const customInstance = axios.create({ baseURL });

    return createRequestMethods(customInstance);
  }, [baseURL, withAuth]);
};

// Export the base instance for interceptor usage
export const api = defaultBaseInstance;
export const authApi = authAxiosInstance;
